import { AuthFormValue, FormValues, INITIAL_FORM_VALUES } from '../../types/auth-form.types'
import { DebouncedState, useDebouncedCallback } from 'use-debounce'
import { Domains } from '@dtx-company/true-common/src/types/domains'
import { FronteggErrorMessage } from '@dtx-company/inter-app/src/components/FronteggErrorMessage/FronteggErrorMessage'
import { FronteggSignupError } from '@dtx-company/ithaca-sdk/src/frontegg/error'
import { IthacaRelatedPlanId, useAddUserToPlanExtra } from '../../hooks/useAddUserToPlanExtra'
import {
  NEW_SIGN_UP_ERROR_MESSAGE,
  SIGN_IN_ERROR_MESSAGE,
  SIGN_UP_ERROR_MESSAGE,
  USER_ALREADY_EXIST_ERROR_MESSAGE
} from '@dtx-company/true-common/src/constants/auth'
import { SSOLoginOrSignupState } from '@dtx-company/inter-app/src/hooks/sso'
import { UseFormReturn, useForm } from 'react-hook-form'
import { authFormYupValidation } from '../../containers/upgrade/validation'
import { logger } from '@dtx-company/logger'
import { unsubscribeFromMarketingEmails } from '@dtx-company/inter-app/src/utils/notifications'
import { useCheckEU } from '@app/common/src/hooks/useCheckEU'
import { useLogin, useLoginExistingUser } from '@dtx-company/inter-app/src/hooks/auth/useLogin'
import { usePrelogin } from '@dtx-company/inter-app/src/hooks/auth/usePrelogin'
import { useRouter } from 'next/router'
import { useSignup } from '@dtx-company/inter-app/src/hooks/auth/useSignup'
import React, { useMemo } from 'react'
import events from '@dtx-company/inter-app/src/event-tracking/events/homepage'

export type UseUpdateAuthFormState = Pick<
  SSOLoginOrSignupState,
  'emailIsSSOEnabled' | 'checkIfEmailSSOEnabled' | 'ssoRedirectHtml' | 'startSSORedirect'
> &
  Pick<UseFormReturn<FormValues>, 'register' | 'watch' | 'handleSubmit' | 'control'> & {
    error: React.ReactNode
    errors: UseFormReturn<FormValues>['formState']['errors']
    submitButtonLabel: string
    checkForValidEmail: DebouncedState<(isValid: boolean) => void>
    isAuthenticating: boolean
    authenticateUser: (values: FormValues) => Promise<void>
    requireEmailOptIn: boolean
  }

export function useUpdateAuthFormState({
  isSignUp,
  onSuccess
}: {
  isSignUp: boolean
  onSuccess?: (email?: string | null) => void
}): UseUpdateAuthFormState {
  const [isAuthenticating, setIsAuthenticating] = React.useState(false)
  const [requireEmailOptIn, setRequireEmailOptIn] = React.useState(false)
  const [error, setError] = React.useState<React.ReactNode>('')
  const router = useRouter()
  const signup = useSignup()
  const login = useLogin()
  const { loginExistingUser } = useLoginExistingUser()
  const { addUserToPlanExtra } = useAddUserToPlanExtra()
  const { emailIsSSOEnabled, checkIfEmailSSOEnabled, ssoRedirectHtml, startSSORedirect } =
    usePrelogin()

  const {
    register,
    formState: { errors },
    watch,
    handleSubmit,
    control
  } = useForm<FormValues>({
    defaultValues: INITIAL_FORM_VALUES,
    resolver: authFormYupValidation(isSignUp, emailIsSSOEnabled),
    reValidateMode: 'onChange',
    shouldUnregister: true
  })

  const checkEUCallback = React.useCallback(() => {
    setRequireEmailOptIn(true)
  }, [])
  useCheckEU(checkEUCallback)

  const authenticateUser = async (values: FormValues): Promise<void> => {
    setIsAuthenticating(true)
    setError('')
    try {
      const authHandler = isSignUp ? signup : login
      const { email, password } = values
      const user = await authHandler({ email, password, product: Domains.FLOWCODE })

      const hasUserOptedInToEmails = requireEmailOptIn
        ? values[AuthFormValue.MARKETING_OPT_IN]
        : !values[AuthFormValue.MARKETING_OPT_IN]

      if (isSignUp && user?.email && !hasUserOptedInToEmails) {
        await unsubscribeFromMarketingEmails(user.email)
      }

      if (isSignUp && router.query.tc === 'amex') {
        await addUserToPlanExtra(IthacaRelatedPlanId.AMEX_DESIGN)
      }

      onSuccess?.(email)
    } catch (e) {
      let errorMessage: React.ReactNode = isSignUp ? SIGN_UP_ERROR_MESSAGE : SIGN_IN_ERROR_MESSAGE
      if (e.code === 'auth/email-already-in-use') {
        errorMessage = 'Email already in use. Please sign in to your account to continue'
      } else if (e.message === USER_ALREADY_EXIST_ERROR_MESSAGE) {
        const login = await loginExistingUser(
          values[AuthFormValue.EMAIL],
          values[AuthFormValue.PASSWORD]
        )

        errorMessage = NEW_SIGN_UP_ERROR_MESSAGE
        if (login.errorMessage) errorMessage = login.errorMessage
      } else if (e instanceof FronteggSignupError) {
        errorMessage = FronteggErrorMessage({ error: e })
      } else {
        logger.logError(`Unknown authentication error: ${e}`, { technicalArea: 'authentication' })
      }

      setError(errorMessage)
    } finally {
      setIsAuthenticating(false)
    }
  }

  const checkForValidEmail = useDebouncedCallback((isValid: boolean) => {
    if (isValid) {
      events.userEnteredValidEmail()
    }
  }, 500)

  const submitButtonLabel = useMemo((): string => {
    if (emailIsSSOEnabled) {
      return 'Sign in with SSO'
    }
    return isSignUp ? 'Sign up' : 'Sign in'
  }, [emailIsSSOEnabled, isSignUp])

  return {
    submitButtonLabel,
    checkForValidEmail,
    isAuthenticating,
    authenticateUser,
    checkIfEmailSSOEnabled,
    emailIsSSOEnabled,
    ssoRedirectHtml,
    startSSORedirect,
    register,
    handleSubmit,
    errors,
    watch,
    control,
    error,
    requireEmailOptIn
  }
}
